import React from 'react';
import { Shadow, Gradient } from 'fabric';
import { Box, Typography, Slider, Divider, TextField, MenuItem, Switch, FormControlLabel, useTheme } from '@mui/material';
import { usePhotoAddsStore } from '../usePhotoAddsStoreBridge';

const BlendPanel = () => {
  const theme = useTheme();
  const { canvas, updateLayersFromCanvas } = usePhotoAddsStore();

  const getActive = () => canvas?.getActiveObject?.();
  const [shadowEnabled, setShadowEnabled] = React.useState(false);
  const [shadowColor, setShadowColor] = React.useState(theme.palette.common.black);
  const [shadowBlur, setShadowBlur] = React.useState(10);
  const [shadowOffsetX, setShadowOffsetX] = React.useState(5);
  const [shadowOffsetY, setShadowOffsetY] = React.useState(5);
  const [shadowOpacity, setShadowOpacity] = React.useState(50);

  const [gradientEnabled, setGradientEnabled] = React.useState(false);
  const [gradColor1, setGradColor1] = React.useState(theme.palette.common.white);
  const [gradColor2, setGradColor2] = React.useState(theme.palette.common.black);

  const syncFromActive = React.useCallback(() => {
    const obj = getActive();
    if (!obj) {
      setShadowEnabled(false);
      setGradientEnabled(false);
      return;
    }
    // Initialize UI from object state
    if (obj.shadow) {
      setShadowEnabled(true);
      const col = obj.shadow.color || 'rgba(0,0,0,0.5)';
      setShadowColor(typeof col === 'string' && col.startsWith('#') ? col : theme.palette.common.black);
      setShadowBlur(obj.shadow.blur ?? 10);
      setShadowOffsetX(obj.shadow.offsetX ?? 5);
      setShadowOffsetY(obj.shadow.offsetY ?? 5);
      if (typeof col === 'string' && col.startsWith('rgba')) {
        const a = parseFloat(col.split(',')[3]);
        setShadowOpacity(Math.round((isNaN(a) ? 0.5 : a) * 100));
      }
    } else {
      setShadowEnabled(false);
    }

    // gradient fill check (simple linear vertical we create)
    const fill = obj.fill;
    if (fill && typeof fill === 'object' && fill.type === 'linear') {
      setGradientEnabled(true);
      const cs = fill.colorStops || [];
      if (cs[0]?.color) setGradColor1(cs[0].color);
      if (cs[cs.length - 1]?.color) setGradColor2(cs[cs.length - 1].color);
    } else {
      setGradientEnabled(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas]);

  React.useEffect(() => {
    if (!canvas) return;
    // Initial sync when canvas ref changes
    syncFromActive();
    const handler = () => syncFromActive();
    const events = ['selection:created', 'selection:updated', 'selection:cleared', 'object:modified'];
    events.forEach((ev) => canvas.on(ev, handler));
    return () => {
      events.forEach((ev) => canvas.off(ev, handler));
    };
  }, [canvas, syncFromActive]);

  const applyShadow = (enabled, opts = {}) => {
    const obj = getActive();
    if (!obj) return;
    if (enabled) {
      const a = (opts.opacity ?? shadowOpacity) / 100;
      const rgba = hexToRgba(opts.color ?? shadowColor, a);
      obj.set('shadow', new Shadow({
        color: rgba,
        blur: opts.blur ?? shadowBlur,
        offsetX: opts.offsetX ?? shadowOffsetX,
        offsetY: opts.offsetY ?? shadowOffsetY,
      }));
    } else {
      obj.set('shadow', null);
    }
    canvas.renderAll();
    updateLayersFromCanvas?.();
  };

  const applyGradient = (enabled, c1, c2) => {
    const obj = getActive();
    if (!obj) return;
    if (enabled) {
      const grad = new Gradient({
        type: 'linear',
        gradientUnits: 'pixels',
        coords: { x1: 0, y1: 0, x2: 0, y2: obj.height || 100 },
        colorStops: [
          { offset: 0, color: c1 || gradColor1 },
          { offset: 1, color: c2 || gradColor2 },
        ],
      });
      // Prefer fill for shapes/text; for line, set stroke gradient
      if (obj.type === 'line') obj.set('stroke', grad);
      else obj.set('fill', grad);
    } else {
      // remove gradient by leaving existing fill color as-is if possible
      // noop; user can set fill from Properties
    }
    canvas.renderAll();
    updateLayersFromCanvas?.();
  };

  const hexToRgba = (hex, alpha = 1) => {
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <Box sx={{ color: theme.palette.text.primary, p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Drop Shadow</Typography>
      <FormControlLabel
        control={<Switch checked={shadowEnabled} onChange={(e) => { const en = e.target.checked; setShadowEnabled(en); applyShadow(en); }} />}
        label="Enabled"
      />
      {shadowEnabled && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Color</Typography>
          <input type="color" value={shadowColor} onChange={(e) => { setShadowColor(e.target.value); applyShadow(true, { color: e.target.value }); }} />
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Opacity</Typography>
          <Slider min={0} max={100} value={shadowOpacity} valueLabelDisplay="auto" onChange={(e, v) => { const val = Array.isArray(v) ? v[0] : v; setShadowOpacity(val); applyShadow(true, { opacity: val }); }} />
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Blur</Typography>
          <Slider min={0} max={100} value={shadowBlur} valueLabelDisplay="auto" onChange={(e, v) => { const val = Array.isArray(v) ? v[0] : v; setShadowBlur(val); applyShadow(true, { blur: val }); }} />
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Offset X</Typography>
          <Slider min={-100} max={100} value={shadowOffsetX} valueLabelDisplay="auto" onChange={(e, v) => { const val = Array.isArray(v) ? v[0] : v; setShadowOffsetX(val); applyShadow(true, { offsetX: val }); }} />
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Offset Y</Typography>
          <Slider min={-100} max={100} value={shadowOffsetY} valueLabelDisplay="auto" onChange={(e, v) => { const val = Array.isArray(v) ? v[0] : v; setShadowOffsetY(val); applyShadow(true, { offsetY: val }); }} />
          <Divider sx={{ mt: 2, mb: 1, borderColor: theme.palette.divider }} />
        </Box>
      )}

      <Typography variant="subtitle2" gutterBottom>Gradient Overlay</Typography>
      <FormControlLabel
        control={<Switch checked={gradientEnabled} onChange={(e) => { const en = e.target.checked; setGradientEnabled(en); applyGradient(en); }} />}
        label="Enabled"
      />
      {gradientEnabled && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Start Color</Typography>
          <input type="color" value={gradColor1} onChange={(e) => { setGradColor1(e.target.value); applyGradient(true, e.target.value, undefined); }} />
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>End Color</Typography>
          <input type="color" value={gradColor2} onChange={(e) => { setGradColor2(e.target.value); applyGradient(true, undefined, e.target.value); }} />
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Direction</Typography>
          <TextField select size="small" value={'vertical'} onChange={(e) => {
            // For now only vertical; placeholder for future
            applyGradient(true);
          }} sx={{ ml: 1 }}>
            <MenuItem value={'vertical'}>Vertical</MenuItem>
          </TextField>
        </Box>
      )}
    </Box>
  );
};

export default BlendPanel;
